import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Select,
  Textarea,
  Divider,
  Box,
  Paper,
  Badge,
  Alert,
  Loader,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { IconSearch, IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  SendOut,
  SendoutRequestType,
  ExternalInstitutionReason,
  SendOutPriority,
  CreateSendOutInput,
} from "../../services/sendOuts.service";
import {
  useExternalInstitutions,
  useCreateSendOut,
  useResubmitSendOut,
} from "../../hooks/useSendOuts";
import { useCaseSpecimens } from "../../hooks/useCases";
import { api } from "../../lib";
import {
  required,
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";
import {
  getLabTodayDateOnly,
  parseDateOnly,
  requireDateOnlyString,
  type DateOnlyString,
} from "../../utils/dateOnly";
import MaterialPicker, { MaterialPickerSelection } from "./MaterialPicker";

// ==================== TYPES ====================

interface Case {
  id: string;
  caseNumber: string;
  patient?: { firstName: string; lastName: string } | null;
  currentStatus?: { name: string } | null;
  isLegacyImported?: boolean;
}

interface LogNewSendOutModalProps {
  opened: boolean;
  onClose: () => void;
  existingSendOut?: SendOut | null;
  onSuccess?: () => void;
  initialCase?: Case;
}

interface FormValues {
  requestType: SendoutRequestType;
  caseId: string;
  externalInstitutionId: string;
  orderingInstitutionId: string;
  externalInstitutionReason: string;
  externalInstitutionReasonOther: string;
  contactPerson: string;
  contactPhoneFax: string;
  priority: string;
  expectedReturnDate: DateOnlyString | null;
  authorizationReference: string;
  specialInstructions: string;
}

// ==================== CONSTANTS ====================

const REASON_OPTIONS = [
  {
    value: ExternalInstitutionReason.CONTINUATION_OF_CARE,
    label: "Continuation of Care",
  },
  {
    value: ExternalInstitutionReason.LEGAL_REQUEST,
    label: "Legal Request",
  },
  {
    value: ExternalInstitutionReason.OUTSIDE_REQUEST_ADDITIONAL_TESTING,
    label: "Outside Request for Additional Testing",
  },
  { value: ExternalInstitutionReason.OTHER, label: "Other" },
];

const PRIORITY_OPTIONS = [
  { value: SendOutPriority.ROUTINE, label: "Routine" },
  { value: SendOutPriority.URGENT, label: "Urgent" },
  { value: SendOutPriority.STAT, label: "Stat" },
];

const REQUEST_TYPE_OPTIONS = [
  { value: SendoutRequestType.EXTERNAL_INSTITUTION, label: "External Request" },
  { value: SendoutRequestType.MATERIAL_RETURN, label: "Material Return" },
];

function filterCasesForRequestType(
  cases: Case[],
  requestType: SendoutRequestType
): Case[] {
  const eligibleStatus =
    requestType === SendoutRequestType.MATERIAL_RETURN
      ? "Completed"
      : "Signed Off";
  return cases.filter(
    (caseItem) =>
      caseItem.currentStatus?.name === eligibleStatus ||
      (caseItem.isLegacyImported === true &&
        caseItem.currentStatus?.name === "Completed")
  );
}

// ==================== COMPONENT ====================

export default function LogNewSendOutModal({
  opened,
  onClose,
  existingSendOut,
  onSuccess,
  initialCase,
}: LogNewSendOutModalProps) {
  const isResubmit = !!existingSendOut;

  const [caseSearch, setCaseSearch] = useState(
    existingSendOut?.case?.caseNumber || initialCase?.caseNumber || ""
  );
  const [debouncedSearch] = useDebouncedValue(caseSearch, 400);
  const [caseResults, setCaseResults] = useState<Case[]>([]);
  const [selectedCase, setSelectedCase] = useState<Case | null>(
    existingSendOut?.case
      ? {
          id: existingSendOut.caseId,
          caseNumber: existingSendOut.case.caseNumber || "",
          patient: existingSendOut.case.patient || null,
        }
      : initialCase ?? null
  );
  const [caseSearchLoading, setCaseSearchLoading] = useState(false);

  const [materialsSelection, setMaterialsSelection] =
    useState<MaterialPickerSelection>(() => ({
      blockIds: new Set(existingSendOut?.blocksSent || []),
      slideIds: new Set(existingSendOut?.slidesSent || []),
    }));

  const { data: institutions = [] } = useExternalInstitutions();
  const createMutation = useCreateSendOut();
  const resubmitMutation = useResubmitSendOut();
  const labToday = useMemo(() => getLabTodayDateOnly(), []);

  const { data: specimens = [], isLoading: specimensLoading } =
    useCaseSpecimens(selectedCase?.id);

  const handleMaterialsChange = useCallback((next: MaterialPickerSelection) => {
    setMaterialsSelection(next);
  }, []);

  const form = useForm<FormValues>({
    initialValues: existingSendOut
      ? {
          requestType: existingSendOut.requestType,
          caseId: existingSendOut.caseId,
          externalInstitutionId: existingSendOut.externalInstitutionId || "",
          orderingInstitutionId: existingSendOut.orderingInstitutionId || "",
          externalInstitutionReason:
            existingSendOut.externalInstitutionReason || "",
          externalInstitutionReasonOther:
            existingSendOut.externalInstitutionReasonOther || "",
          contactPerson: existingSendOut.contactPerson || "",
          contactPhoneFax: existingSendOut.contactPhoneFax || "",
          priority: existingSendOut.priority || "",
          expectedReturnDate: existingSendOut.expectedReturnDate
            ? parseDateOnly(existingSendOut.expectedReturnDate) ?? null
            : null,
          authorizationReference: existingSendOut.authorizationReference || "",
          specialInstructions: existingSendOut.specialInstructions || "",
        }
      : {
          requestType: SendoutRequestType.EXTERNAL_INSTITUTION,
          caseId: initialCase?.id ?? "",
          externalInstitutionId: "",
          orderingInstitutionId: "",
          externalInstitutionReason: "",
          externalInstitutionReasonOther: "",
          contactPerson: "",
          contactPhoneFax: "",
          priority: "",
          expectedReturnDate: null,
          authorizationReference: "",
          specialInstructions: "",
        },
    validate: {
      caseId: (value) =>
        !isResubmit && !value ? "Please select a case" : null,
      externalInstitutionId: required("Destination institution is required"),
      externalInstitutionReason: required("Reason is required"),
      externalInstitutionReasonOther: (value, values) =>
        values.externalInstitutionReason === ExternalInstitutionReason.OTHER &&
        !value?.trim()
          ? "Please describe the reason"
          : null,
      contactPerson: required("Contact person is required"),
      contactPhoneFax: required("Phone/fax is required"),
      priority: required("Priority is required"),
      expectedReturnDate: (value) => {
        if (!value) return null;
        const expectedReturnDate = parseDateOnly(value);
        if (!expectedReturnDate) return "Select a valid expected return date";
        return expectedReturnDate < labToday
          ? "Expected return date cannot be in the past"
          : null;
      },
    },
    validateInputOnBlur: true,
  });

  const institutionOptions = useMemo(
    () =>
      institutions
        .filter((i) => i.isActive)
        .map((i) => ({ value: i.id, label: i.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [institutions]
  );

  const handleRequestTypeChange = useCallback(
    (value: string | null) => {
      const requestType =
        (value as SendoutRequestType | null) ??
        SendoutRequestType.EXTERNAL_INSTITUTION;
      form.setFieldValue("requestType", requestType);
      form.setFieldValue("caseId", "");
      setSelectedCase(null);
      setCaseResults([]);
      setCaseSearch("");
    },
    [form]
  );

  const handleExpectedReturnDateChange = useCallback(
    (value: string | null) => {
      if (value === null) {
        form.setFieldValue("expectedReturnDate", null);
        form.clearFieldError("expectedReturnDate");
        return;
      }

      const expectedReturnDate = parseDateOnly(value);
      if (!expectedReturnDate) {
        form.setFieldError(
          "expectedReturnDate",
          "Select a valid expected return date"
        );
        return;
      }

      form.setFieldValue("expectedReturnDate", expectedReturnDate);
      form.clearFieldError("expectedReturnDate");
    },
    [form]
  );

  const handleExpectedReturnDateBlur = useCallback(() => {
    form.validateField("expectedReturnDate");
  }, [form]);

  // Search for cases eligible for the selected send-out type.
  useEffect(() => {
    if (!debouncedSearch || isResubmit) return;

    setCaseSearchLoading(true);
    api
      .get<{ data: Case[]; total: number }>(
        `/cases?eligibleForSendOut=true&search=${encodeURIComponent(
          debouncedSearch
        )}&limit=10`
      )
      .then((result) => {
        const items = Array.isArray(result)
          ? result
          : (result as any)?.data || [];
        setCaseResults(
          filterCasesForRequestType(items, form.values.requestType)
        );
      })
      .catch(() => setCaseResults([]))
      .finally(() => setCaseSearchLoading(false));
  }, [debouncedSearch, form.values.requestType, isResubmit]);

  const handleCaseSelect = (c: Case) => {
    setSelectedCase(c);
    form.setFieldValue("caseId", c.id);
    setCaseResults([]);
    setCaseSearch(c.caseNumber);
    setMaterialsSelection({ blockIds: new Set(), slideIds: new Set() });
  };

  const handleBarcodeSearch = async (barcode: string) => {
    setCaseSearchLoading(true);
    try {
      const result = await api.get<{ data: Case[]; total: number }>(
        `/cases?eligibleForSendOut=true&search=${encodeURIComponent(
          barcode
        )}&limit=10`
      );
      const items = Array.isArray(result)
        ? result
        : (result as any)?.data || [];
      const eligibleCases = filterCasesForRequestType(
        items,
        form.values.requestType
      );
      if (eligibleCases.length === 1) {
        handleCaseSelect(eligibleCases[0]);
      } else if (eligibleCases.length > 1) {
        setCaseResults(eligibleCases);
      } else {
        form.setFieldError("caseId", "No eligible case found for this barcode");
      }
    } catch {
      setCaseResults([]);
    } finally {
      setCaseSearchLoading(false);
    }
  };

  const handleSubmit = form.onSubmit(async (values) => {
    const payload: CreateSendOutInput = {
      caseId: values.caseId || existingSendOut?.caseId || "",
      requestType: values.requestType,
      externalInstitutionId: values.externalInstitutionId,
      externalInstitutionReason:
        values.externalInstitutionReason as ExternalInstitutionReason,
      externalInstitutionReasonOther:
        values.externalInstitutionReason === ExternalInstitutionReason.OTHER
          ? values.externalInstitutionReasonOther
          : undefined,
      orderingInstitutionId: values.orderingInstitutionId || undefined,
      contactPerson: values.contactPerson,
      contactPhoneFax: values.contactPhoneFax,
      priority: values.priority as SendOutPriority,
      expectedReturnDate: values.expectedReturnDate
        ? requireDateOnlyString(
            values.expectedReturnDate,
            "Expected return date"
          )
        : undefined,
      authorizationReference: values.authorizationReference || undefined,
      specialInstructions: values.specialInstructions || undefined,
      slidesSent: Array.from(materialsSelection.slideIds),
      blocksSent: Array.from(materialsSelection.blockIds),
    };

    try {
      if (isResubmit && existingSendOut) {
        await resubmitMutation.mutateAsync({
          id: existingSendOut.id,
          data: payload,
        });
        notifications.show({
          title: "Resubmitted",
          message: "Send-out resubmitted and pending approval",
          color: "blue",
        });
      } else {
        await createMutation.mutateAsync(payload);
        notifications.show({
          title: "Send-Out Logged",
          message: "Send-out created and pending approval",
          color: "green",
        });
      }
      onSuccess?.();
      form.reset();
      setSelectedCase(null);
      setCaseSearch("");
      setCaseResults([]);
      setMaterialsSelection({ blockIds: new Set(), slideIds: new Set() });
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to submit send-out";
      showValidationNotification(message);
      scrollToFirstError(form);
    }
  });

  const isUpdatingEditorRef = useRef(false);
  const lastKeyTimeRef = useRef<number>(0);
  const barcodeBufferRef = useRef<boolean>(false);

  const specialInstructionsEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Any special handling instructions...",
      }),
      Underline,
    ],
    content: existingSendOut?.specialInstructions || "",
    onUpdate: ({ editor: ed }) => {
      if (isUpdatingEditorRef.current) return;
      form.setFieldValue("specialInstructions", ed.getHTML());
    },
  });

  // Sync editor when form value changes externally (resubmit pre-fill / reset)
  useEffect(() => {
    if (!specialInstructionsEditor) return;
    const formVal = form.values.specialInstructions || "";
    const editorHtml = specialInstructionsEditor.getHTML();
    if (editorHtml !== formVal) {
      isUpdatingEditorRef.current = true;
      specialInstructionsEditor.commands.setContent(formVal);
      isUpdatingEditorRef.current = false;
    }
  }, [form.values.specialInstructions, specialInstructionsEditor]);

  const isLoading = createMutation.isPending || resubmitMutation.isPending;
  const showOtherReason =
    form.values.externalInstitutionReason === ExternalInstitutionReason.OTHER;

  const patientName = selectedCase?.patient
    ? `${selectedCase.patient.firstName} ${selectedCase.patient.lastName}`
    : null;

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      size="lg"
      closeOnClickOutside={false}
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
        header: { flexShrink: 0 },
        body: { overflowY: "auto", flex: 1 },
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            {isResubmit ? "Edit & Resubmit Send-Out" : "Log New Send-Out"}
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <form id="send-out-form" onSubmit={handleSubmit}>
            <Stack gap="md">
              <Select
                label="Request Type"
                data={REQUEST_TYPE_OPTIONS}
                withAsterisk
                disabled={isResubmit}
                value={form.values.requestType}
                onChange={handleRequestTypeChange}
              />

              {/* Rejection reason (resubmit mode) */}
              {isResubmit && existingSendOut?.rejectionReason && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  title="Rejection Reason"
                >
                  {existingSendOut.rejectionReason}
                </Alert>
              )}

              {/* Case Search */}
              {!isResubmit ? (
                <Box>
                  <TextInput
                    label="Case Search"
                    placeholder="Search by case # or patient name... (or scan barcode)"
                    description="Barcode scanner supported — scan will auto-select the case"
                    value={caseSearch}
                    onChange={(e) => {
                      setCaseSearch(e.target.value);
                      if (selectedCase) {
                        setSelectedCase(null);
                        form.setFieldValue("caseId", "");
                      }
                    }}
                    onKeyDown={(e) => {
                      const now = Date.now();
                      if (now - lastKeyTimeRef.current < 50) {
                        barcodeBufferRef.current = true;
                      }
                      lastKeyTimeRef.current = now;
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (barcodeBufferRef.current && caseSearch.trim()) {
                          handleBarcodeSearch(caseSearch.trim());
                        }
                        barcodeBufferRef.current = false;
                      }
                    }}
                    leftSection={<IconSearch size={16} />}
                    rightSection={
                      caseSearchLoading ? <Loader size="xs" /> : null
                    }
                    withAsterisk
                    error={form.errors.caseId}
                  />
                  {caseResults.length > 0 && !selectedCase && (
                    <Paper
                      withBorder
                      mt={4}
                      style={{ maxHeight: 200, overflowY: "auto" }}
                    >
                      <Stack gap={0}>
                        {caseResults.map((c) => (
                          <Box
                            key={c.id}
                            p="xs"
                            style={{ cursor: "pointer" }}
                            onClick={() => handleCaseSelect(c)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "var(--mantine-color-gray-1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <Group gap="xs">
                              <Text fw={600} size="sm">
                                {c.caseNumber}
                              </Text>
                              {c.patient && (
                                <Text size="sm" c="dimmed">
                                  {c.patient.firstName} {c.patient.lastName}
                                </Text>
                              )}
                            </Group>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  )}
                  {selectedCase && (
                    <Paper withBorder mt={4} p="xs" bg="green.0">
                      <Group gap="xs">
                        <Text fw={600} size="sm">
                          {selectedCase.caseNumber}
                        </Text>
                        {patientName && (
                          <Text size="sm" c="dimmed">
                            — {patientName}
                          </Text>
                        )}
                        <Badge size="xs" color="green">
                          {selectedCase.currentStatus?.name ?? "Eligible"}
                        </Badge>
                      </Group>
                    </Paper>
                  )}
                </Box>
              ) : (
                <Paper withBorder p="xs" bg="blue.0">
                  <Group gap="xs">
                    <Text fw={600} size="sm">
                      {selectedCase?.caseNumber ||
                        existingSendOut?.case?.caseNumber}
                    </Text>
                    {patientName && (
                      <Text size="sm" c="dimmed">
                        — {patientName}
                      </Text>
                    )}
                  </Group>
                </Paper>
              )}

              <Divider />

              {/* Reason */}
              <Select
                label="Reason"
                placeholder="Select reason..."
                data={REASON_OPTIONS}
                withAsterisk
                {...form.getInputProps("externalInstitutionReason")}
              />

              {showOtherReason && (
                <Textarea
                  label="Describe Reason"
                  placeholder="Describe the reason for this send-out..."
                  withAsterisk
                  minRows={2}
                  {...form.getInputProps("externalInstitutionReasonOther")}
                />
              )}

              {/* Destination institution */}
              <Select
                label="Destination Institution"
                placeholder="Select institution..."
                data={institutionOptions}
                searchable
                withAsterisk
                {...form.getInputProps("externalInstitutionId")}
              />

              {/* Ordering institution (optional) */}
              <Select
                label="Ordering Institution (Optional)"
                placeholder="Select institution..."
                data={institutionOptions}
                searchable
                clearable
                {...form.getInputProps("orderingInstitutionId")}
              />

              {/* Contact person + phone/fax */}
              <Group grow>
                <TextInput
                  label="Contact Person"
                  placeholder="Contact name..."
                  withAsterisk
                  {...form.getInputProps("contactPerson")}
                />
                <TextInput
                  label="Phone / Fax"
                  placeholder="Phone or fax number..."
                  withAsterisk
                  {...form.getInputProps("contactPhoneFax")}
                />
              </Group>

              {/* Priority */}
              <Select
                label="Priority"
                placeholder="Select priority..."
                data={PRIORITY_OPTIONS}
                withAsterisk
                {...form.getInputProps("priority")}
              />

              {/* Expected Return Date (optional) */}
              <DateInput
                label="Expected Return Date (Optional)"
                placeholder="Select date..."
                clearable
                valueFormat="MMM D, YYYY"
                minDate={labToday}
                value={form.values.expectedReturnDate}
                error={form.errors.expectedReturnDate}
                onChange={handleExpectedReturnDateChange}
                onBlur={handleExpectedReturnDateBlur}
              />

              {/* Authorization Reference (optional) */}
              <TextInput
                label="Authorization Reference (Optional)"
                placeholder="Authorization or reference number..."
                {...form.getInputProps("authorizationReference")}
              />

              {/* Special Instructions (optional) */}
              <Box>
                <Text size="sm" fw={500} mb={4}>
                  Special Instructions (Optional)
                </Text>
                <RichTextEditor editor={specialInstructionsEditor}>
                  <RichTextEditor.Toolbar>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.Bold />
                      <RichTextEditor.Italic />
                      <RichTextEditor.Underline />
                    </RichTextEditor.ControlsGroup>
                    <RichTextEditor.ControlsGroup>
                      <RichTextEditor.BulletList />
                      <RichTextEditor.OrderedList />
                    </RichTextEditor.ControlsGroup>
                  </RichTextEditor.Toolbar>
                  <RichTextEditor.Content />
                </RichTextEditor>
              </Box>

              {/* Materials to Send (optional) */}
              {selectedCase && (
                <Box>
                  <Text size="sm" fw={500} mb={4}>
                    Materials to Send (Optional)
                  </Text>
                  {specimensLoading ? (
                    <Group gap="xs">
                      <Loader size="xs" />
                      <Text size="xs" c="dimmed">
                        Loading specimens...
                      </Text>
                    </Group>
                  ) : (
                    <MaterialPicker
                      specimens={specimens}
                      selection={materialsSelection}
                      onChange={handleMaterialsChange}
                      helperText="Leave blank if the request did not specify."
                      emptyMessage="This case has no blocks or slides recorded yet."
                    />
                  )}
                </Box>
              )}
            </Stack>
          </form>
        </Modal.Body>
        <Box
          px="md"
          py="sm"
          style={{
            borderTop: "1px solid var(--mantine-color-default-border)",
            flexShrink: 0,
          }}
        >
          <Group justify="space-between">
            <Button variant="subtle" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" form="send-out-form" loading={isLoading}>
              {isResubmit ? "Resubmit" : "Log Send-Out"}
            </Button>
          </Group>
        </Box>
      </Modal.Content>
    </Modal.Root>
  );
}
